import React, { useState, useEffect, useCallback } from 'react';
import { Reorder } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api, { breedDataAPI, petAPI } from '../../../services/api';
import { showSuccessToast, showErrorToast } from '../../../utils/notificationUtils';
import BreedForm from './BreedForm';
import './BreedManager.css';

const BreedManager = ({ initialView = 'list' }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();

  const [breeds, setBreeds] = useState([]);
  const [species, setSpecies] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // View states: 'list' or 'form'
  const [view, setView] = useState(initialView === 'form' ? 'form' : 'list');
  const [editingBreed, setEditingBreed] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [filterSpecies, setFilterSpecies] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Sync view with initialView prop
  useEffect(() => {
    if (initialView === 'form') {
      setView('form');
      setEditingBreed(null);
    } else {
      setView('list');
    }
  }, [initialView]);

  // Form State
  const emptyForm = {
    species_id: '', 
    breed_type_id: '',
    name_en: '', name_ar: '',
    description_en: '', description_ar: '',
    temperament_en: '', temperament_ar: '',
    appearance_en: '', appearance_ar: '',
    lifespan_en: '', lifespan_ar: '',
    grooming_en: '', grooming_ar: '',
    health_en: '', health_ar: '',
    nutrition_en: '', nutrition_ar: '',
    facts_en: '', facts_ar: '',
    origin_en: '', origin_ar: '',
    image_url: '', is_active: true, sort_order: 0,
    image: null, remove_image: false, existing_image_url: ''
  };

  const [formData, setFormData] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Fetch species independently for the dropdown
      const speciesRes = await api.get(`${import.meta.env.VITE_API_URL}/api/v1/species`);
      const speciesData = speciesRes.data?.data;
      if (Array.isArray(speciesData)) {
        speciesData.sort((a, b) => {
          if (a.is_active === b.is_active) {
            return (a.sort_order || 0) - (b.sort_order || 0);
          }
          return a.is_active ? -1 : 1;
        });
        setSpecies(speciesData);
      }

      // 2. Fetch only the managed breeds from the CMS
      const breedsRes = await breedDataAPI.getBreeds({ 
        species_id: filterSpecies,
        search: searchTerm 
      });
      
      const breedsData = breedsRes.data?.data || (Array.isArray(breedsRes.data) ? breedsRes.data : []);
      setBreeds(breedsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      showErrorToast(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [filterSpecies, searchTerm, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenForm = (breed = null) => {
    if (breed) {
      setEditingBreed(breed);
      setFormData({
        species_id:     breed.species_id || '',
        breed_type_id:  breed.breed_type_id || '',
        name_en:        breed.name_en || '',
        name_ar:        breed.name_ar || '',
        description_en: breed.description_en || '',
        description_ar: breed.description_ar || '',
        temperament_en: breed.temperament_en || '',
        temperament_ar: breed.temperament_ar || '',
        appearance_en:  breed.appearance_en || '',
        appearance_ar:  breed.appearance_ar || '',
        lifespan_en:    breed.lifespan_en || '',
        lifespan_ar:    breed.lifespan_ar || '',
        grooming_en:    breed.grooming_en || '',
        grooming_ar:    breed.grooming_ar || '',
        health_en:      breed.health_en || '',
        health_ar:      breed.health_ar || '',
        nutrition_en:   breed.nutrition_en || '',
        nutrition_ar:   breed.nutrition_ar || '',
        facts_en:       breed.facts_en || '',
        facts_ar:       breed.facts_ar || '',
        origin_en:      breed.origin_en || '',
        origin_ar:      breed.origin_ar || '',
        image_url:      breed.image_url || '',
        is_active:      breed.is_active,
        sort_order:     breed.sort_order || 0,
        image:          null,
        remove_image:   false,
        existing_image_url: breed.profile_image_url || ''
      });
    } else {
      setEditingBreed(null);
      setFormData(emptyForm);
    }
    setView('form');
    // Scroll to top when opening form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadExistingProfile = async (breedDataId) => {
    if (!breedDataId) return;
    
    setLoading(true);
    try {
      const res = await breedDataAPI.getBreed(breedDataId);
      const data = res.data?.data;
      if (data) {
        handleOpenForm(data);
        showSuccessToast(t('cms.existing_profile_loaded', 'Existing profile loaded!'));
      }
    } catch (error) {
      console.error('Error loading existing profile:', error);
      showErrorToast(t('cms.error_loading_profile', 'Failed to load existing profile.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetFormWithBreed = (breedType) => {
    setEditingBreed(null);
    setFormData({
      ...emptyForm,
      species_id: breedType.species_id,
      breed_type_id: breedType.id,
      name_en: breedType.name_en || breedType.name || '',
      name_ar: breedType.name_ar || breedType.name || ''
    });
  };

  const handleCloseForm = () => {
    setView('list');
    setEditingBreed(null);
    navigate('/admin/dashboard/cms/breeds');
  };

  // Handle external view changes (from sidebar)
  useEffect(() => {
    if (initialView === 'form') {
      handleOpenForm();
    } else {
      handleCloseForm();
    }
  }, [initialView]);

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingBreed && editingBreed.id) {
        await breedDataAPI.updateBreed(editingBreed.id, formData);
        showSuccessToast(t('updateSuccess'));
      } else {
        await breedDataAPI.createBreed(formData);
        showSuccessToast(t('createSuccess'));
      }
      handleCloseForm();
      fetchData();
    } catch (error) {
      console.error('Error saving breed:', error);
      showErrorToast(error.response?.data?.message || t('errorOccurred'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    
    if (window.confirm(t('confirmDelete'))) {
      try {
        await breedDataAPI.deleteBreed(id);
        showSuccessToast(t('deleteSuccess'));
        fetchData();
      } catch (error) {
        showErrorToast(t('errorOccurred'));
      }
    }
  };

  const handleReorder = async (newOrder) => {
    setBreeds(newOrder);
    try {
      // Only reorder breeds that actually exist in the CMS database
      const managedIds = newOrder
        .filter(b => b.breed_data_id)
        .map(b => b.breed_data_id);

      if (managedIds.length > 0) {
        await breedDataAPI.reorderBreeds(managedIds);
        showSuccessToast(t('cms.order_updated_successfully', 'Order updated successfully'));
      }
    } catch (error) {
      console.error('Error reordering breeds:', error);
      showErrorToast(t('errorOccurred'));
      // Optional: Refresh data to restore original order from server
      fetchData();
    }
  };

  if (view === 'form') {
    return (
      <BreedForm 
        formData={formData}
        handleInputChange={handleInputChange}
        handleSubmit={handleSubmit}
        handleCancel={handleCloseForm}
        submitting={submitting}
        isEditing={!!editingBreed}
        species={species}
        onLoadExisting={loadExistingProfile}
        onResetFormWithBreed={handleResetFormWithBreed}
      />
    );
  }

  return (
    <div className={`breed-manager-container ${isRTL ? 'rtl' : ''}`}>
      <div className="breed-manager-header">
        <h2 className="admin-dashboard-section-title">{t("adminDashboard.menu.cms_breeds")}</h2>
        <button className="add-breed-btn" onClick={() => navigate('/admin/dashboard/cms/breeds/add')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          {t("cms.add_breed_data", "Add Breed Data")}
        </button>
      </div>

      <div className="breed-manager-filters">
        <div className="search-box">
          <div className="icon-input-wrapper">
            <svg className="input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input 
              type="text" 
              placeholder={t("cms.search_breeds", "Search breeds...")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="search-clear-btn" onClick={() => setSearchTerm('')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
        </div>
        <div className="filter-wrapper">
          <div className="icon-input-wrapper">
            <svg className="input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
            </svg>
            <select 
              value={filterSpecies} 
              onChange={(e) => setFilterSpecies(e.target.value)}
              className="species-filter"
            >
              <option value="">{t("cms.all_species", "All Species")}</option>
              {Array.isArray(species) && species.length > 0 ? (
                species.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name || s.display_name || s.name_en || `Species #${s.id}`}
                  </option>
                ))
              ) : (
                <option disabled>{t("cms.no_species_loaded", "No species loaded...")}</option>
              )}
            </select>
          </div>
        </div>
      </div>

      <div className="breed-list-container">
        {loading ? (
          <div className="loader-container">
            <div className="loader"></div>
          </div>
        ) : breeds.length > 0 ? (
          <div className="breed-table-wrapper">
            <table className="breed-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>{t("cms.image", "Image")}</th>
                  <th>{t("cms.name", "Name")}</th>
                  <th>{t("cms.species", "Species")}</th>
                  <th>{t("cms.status", "Status")}</th>
                  <th style={{ textAlign: 'center' }}>{t("cms.actions", "Actions")}</th>
                </tr>
              </thead>
              <Reorder.Group as="tbody" axis="y" values={breeds} onReorder={handleReorder}>
                {breeds.map(breed => (
                  <Reorder.Item as="tr" key={breed.id} value={breed} className="draggable-row">
                    <td>
                      <div className="drag-handle">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="9" cy="5" r="1"></circle>
                          <circle cx="9" cy="12" r="1"></circle>
                          <circle cx="9" cy="19" r="1"></circle>
                          <circle cx="15" cy="5" r="1"></circle>
                          <circle cx="15" cy="12" r="1"></circle>
                          <circle cx="15" cy="19" r="1"></circle>
                        </svg>
                      </div>
                    </td>
                    <td>
                      <div className="breed-image-preview">
                        {breed.image_url ? (
                          <img src={breed.image_url} alt={breed.name} />
                        ) : (
                          <div className="no-image-placeholder">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                              <circle cx="8.5" cy="8.5" r="1.5"></circle>
                              <polyline points="21 15 16 10 5 21"></polyline>
                            </svg>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="breed-name-cell">
                        <span className="primary-name">{breed.name}</span>
                        <span className="secondary-name">{isRTL ? breed.name_en : breed.name_ar}</span>
                      </div>
                    </td>
                    <td>{breed.species?.name}</td>
                    <td>
                      <span className={`status-badge ${breed.is_active ? 'active' : 'inactive'}`}>
                        {breed.is_active ? t("cms.active", "Active") : t("cms.inactive", "Inactive")}
                      </span>
                    </td>
                    <td align="center">
                      <div className="action-buttons">
                        <button 
                          className="edit-btn" 
                          onClick={() => loadExistingProfile(breed.id)} 
                          title={t('edit')}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                        </button>
                        <button 
                          className="delete-btn" 
                          onClick={() => handleDelete(breed.id)}
                          title={t('delete')}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </Reorder.Item>
                ))}
              </Reorder.Group>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                <line x1="12" y1="22.08" x2="12" y2="12"></line>
              </svg>
            </div>
            <h3>{t("cms.no_breeds_found", "No breeds found")}</h3>
            <p>{t("cms.try_adjusting_filters", "Try adjusting your filters or search criteria to find what you're looking for.")}</p>
            <button className="clear-filters-btn" onClick={() => { setSearchTerm(''); setFilterSpecies(''); }}>
              {t("cms.clear_all_filters", "Clear All Filters")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BreedManager;
